import { useEffect, useState } from 'react'
import { AlertTriangle, Network } from 'lucide-react'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import type { Partner, PartnerFormData, PartnerPyramidPreview } from '@/types'

interface PartnerPyramidFieldsProps {
  formData: PartnerFormData
  setFormData: (data: PartnerFormData) => void
  partners: Partner[]
  onValidityChange?: (valid: boolean) => void
}

export function PartnerPyramidFields({ formData, setFormData, partners, onValidityChange }: PartnerPyramidFieldsProps) {
  const [preview, setPreview] = useState<PartnerPyramidPreview | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      api
        .post(
          '/parceiros/pyramid/preview',
          {
            parceiroId: formData.id,
            modoComissao: formData.modoComissao,
            indicadoPorId: formData.indicadoPorId || null,
            comissao: Number(formData.comissao) || 0,
          },
          { signal: controller.signal },
        )
        .then((resp) => setPreview(resp.data.data))
        .catch(() => {
          if (!controller.signal.aborted) setPreview(null)
        })
    }, 300)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [formData.id, formData.indicadoPorId, formData.comissao, formData.modoComissao])

  useEffect(() => {
    onValidityChange?.(preview ? preview.ok : true)
  }, [preview, onValidityChange])

  const options = partners
    .filter((p) => p.id !== formData.id)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

  const maxPercent = preview?.maxPercent ?? 20
  const used = preview?.usedPercent ?? (Number(formData.comissao) || 0)
  const usedRatio = Math.min(100, (used / maxPercent) * 100)
  const isOneTimeMode = formData.modoComissao === 'DUAS_MENSALIDADES'

  return (
    <div>
      <h4
        className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-3 flex items-center gap-1.5'
        style={{ fontSize: 'var(--font-size-label)' }}
      >
        <Network size={12} />
        Pirâmide de Comissão
      </h4>

      <div className='space-y-3'>
        <label className='block'>
          <span className='block text-[var(--font-color-label)] text-xs font-medium mb-1.5'>Indicado por</span>
          <select
            value={formData.indicadoPorId}
            onChange={(e) => setFormData({ ...formData, indicadoPorId: e.target.value })}
            className='w-full rounded-lg border border-[var(--border)] bg-[var(--bg-main)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)]'
          >
            <option value=''>Ninguém (topo da pirâmide)</option>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome} · {Number(p.comissao)}%
              </option>
            ))}
          </select>
        </label>

        <div className='rounded-lg border border-[var(--border)] bg-[var(--bg-main)] p-3 space-y-2'>
          <div className='flex items-center justify-between text-xs'>
            <span className='text-[var(--font-color-muted)]'>Comissão usada na cadeia</span>
            <span className={cn('font-semibold', preview && !preview.ok ? 'text-rose-500' : 'text-[var(--text-primary)]')}>
              {isOneTimeMode
                ? '100% da 1ª parcela'
                : `${used.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% de ${maxPercent}%`}
            </span>
          </div>
          <div className='h-1.5 rounded-full bg-[var(--glass-bg)] overflow-hidden'>
            <div
              className={cn(
                'h-full rounded-full transition-all',
                preview && !preview.ok ? 'bg-rose-500' : usedRatio >= 90 ? 'bg-amber-500' : 'bg-emerald-500',
              )}
              style={{ width: `${usedRatio}%` }}
            />
          </div>
          {preview && (
            <p className='text-[11px] text-[var(--font-color-muted)]'>
              {isOneTimeMode
                ? 'Indicador/RP recebe somente a primeira parcela. O teto de 20% vale apenas para recorrência.'
                : `Profundidade da cadeia: ${preview.depth} de ${preview.maxLevels} níveis`}
            </p>
          )}
          {preview && !preview.ok && preview.message && (
            <p className='flex items-center gap-1.5 text-[11px] font-medium text-rose-500'>
              <AlertTriangle size={12} />
              {preview.message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
